import React, { useState } from 'react';
import { AiOutlineClose, AiOutlineSave } from 'react-icons/ai';
import FolderTreePicker from '../FolderTreePicker/FolderTreePicker';
import { validateIdentifier, IDENTIFIER_POLICY_HINT } from '../../utils/seriesUtils';

/**
 * Modal de alta de folder. Pide id (validado con la misma regla que series),
 * nombre, y padre opcional (null = raíz del doctree).
 *
 * Props:
 * - open: bool
 * - folders: lista plana {id, name, parent} para el FolderTreePicker
 * - defaultParent: id del folder padre pre-seleccionado (opcional)
 * - onCancel: () => void
 * - onSubmit: ({ id, name, parent }) => Promise<void>
 */
const FolderCreateModal = ({ open, folders, defaultParent, onCancel, onSubmit }) => {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [parent, setParent] = useState(defaultParent || '');
  const [idError, setIdError] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setId('');
      setName('');
      setParent(defaultParent || '');
      setIdError(null);
      setSaving(false);
    }
  }, [open, defaultParent]);

  if (!open) return null;

  const idInvalid = validateIdentifier(id, 'ID del folder') !== null;
  const nameMissing = !name.trim();
  const canSubmit = !idInvalid && !nameMissing && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onSubmit({ id: id.trim(), name: name.trim(), parent: parent || null });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} data-testid="folder-create-modal">
      <form onSubmit={handleSubmit} style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Crear folder</h2>
          <button type="button" onClick={onCancel} style={styles.closeBtn} aria-label="Cerrar">
            <AiOutlineClose />
          </button>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>ID del folder</label>
          <input
            type="text"
            value={id}
            onChange={(e) => { setId(e.target.value.toUpperCase()); if (idError) setIdError(null); }}
            onBlur={(e) => setIdError(validateIdentifier(e.target.value, 'ID del folder'))}
            style={{ ...styles.input, borderColor: idError ? '#DC2626' : '#D1D5DB' }}
            placeholder="Ej: ADM-FACTURACION"
            autoFocus
            data-testid="folder-create-id"
          />
          {idError ? (
            <p style={styles.errorHint}>{idError}</p>
          ) : (
            <p style={styles.policyHint}>{IDENTIFIER_POLICY_HINT}</p>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Nombre</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            placeholder="Ej: Facturación"
            data-testid="folder-create-name"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Folder padre (vacío = raíz)</label>
          <FolderTreePicker
            folders={folders}
            value={parent}
            onChange={setParent}
            placeholder="Sin padre — folder raíz"
          />
        </div>

        <div style={styles.footer}>
          <button type="button" onClick={onCancel} style={styles.btnCancel}>Cancelar</button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{ ...styles.btnPrimary, ...(canSubmit ? {} : styles.btnDisabled) }}
            data-testid="folder-create-submit"
          >
            <AiOutlineSave style={{ marginRight: 6 }} />
            {saving ? 'Creando…' : 'Crear folder'}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  },
  modal: {
    background: '#FFFFFF', padding: '24px', borderRadius: '8px',
    minWidth: '440px', maxWidth: '520px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { margin: 0, fontSize: '18px', color: '#00468B', fontWeight: 'bold' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6B7280' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', color: '#374151', fontWeight: 500 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  policyHint: { margin: '4px 0 0', fontSize: '12px', color: '#6B7280' },
  errorHint: { margin: '4px 0 0', fontSize: '12px', color: '#DC2626' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' },
  btnCancel: { padding: '8px 16px', border: '1px solid #D1D5DB', background: '#FFFFFF', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  btnPrimary: { padding: '8px 16px', background: '#00468B', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'inline-flex', alignItems: 'center' },
  btnDisabled: { background: '#9CA3AF', cursor: 'not-allowed' },
};

export default FolderCreateModal;
